import { useState } from 'react';
import { CloudOff } from 'lucide-react';
import { toast } from 'sonner';
import { AppAlert } from '@/components/AppAlert';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/use-auth';
import { retrySessionRefresh } from '@/lib/api';
import { buildLoginPath } from '@/lib/auth-redirect';
import { useAuthStore } from '@/stores/authStore';

/**
 * jflo, or the session renewal, didn't answer. A network or server error never signs anyone out
 * (decision 11 a in the T-150 audit): only jflo saying the session is over does.
 */
export function SessionUnreachableNotice({
  signedIn = true,
  retrying = false,
  signingOut = false,
  onRetry,
  onSignOut,
  className,
}: {
  /** False on /login, where there is no session to keep. */
  signedIn?: boolean;
  retrying?: boolean;
  signingOut?: boolean;
  onRetry: () => void;
  onSignOut?: () => void;
  className?: string;
}) {
  return (
    <AppAlert
      tone="warning"
      role="status"
      icon={<CloudOff className="size-4" />}
      title="Can't reach jflo"
      className={className}
      action={
        <div className="flex items-center gap-2">
          <Button
            size="xs"
            variant="outline"
            loading={retrying}
            loadingText="Retrying…"
            disabled={signingOut}
            onClick={onRetry}
          >
            Retry
          </Button>
          {onSignOut ? (
            <Button
              size="xs"
              variant="ghost"
              loading={signingOut}
              loadingText="Signing out…"
              disabled={retrying}
              onClick={onSignOut}
            >
              Sign out
            </Button>
          ) : null}
        </div>
      }
    >
      Check your connection, then try again.{signedIn ? " You're still signed in." : ''}
    </AppAlert>
  );
}

/** In place of the page when the session check at load got no answer (app.tsx route guards). */
export function SessionUnreachableScreen() {
  const { user, retrySession, logout } = useAuth();
  const [signingOut, setSigningOut] = useState(false);

  const signOut = async () => {
    // Read before signing out: the route guard moves to /login once the user is gone. Keeps this
    // page, including /extension/connect's redirect_uri, for after sign-in.
    const loginPath = buildLoginPath();
    setSigningOut(true);
    await logout(loginPath);
  };

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-muted/40 p-4">
      <div className="w-full max-w-md">
        <SessionUnreachableNotice
          // A new tab has no stored user yet, so it can't say it is still signed in.
          signedIn={Boolean(user)}
          signingOut={signingOut}
          onRetry={retrySession}
          onSignOut={() => void signOut()}
        />
      </div>
    </div>
  );
}

/** Above the page when renewing the session failed mid-session; the page and its edits stay. */
export function SessionRefreshBanner({ className }: { className?: string }) {
  const failed = useAuthStore((s) => s.sessionRefreshFailed);
  const [retrying, setRetrying] = useState(false);

  if (!failed) return null;

  const retry = async () => {
    setRetrying(true);
    // 'ok' clears the flag, which hides this; 'signed-out' goes to /login.
    const outcome = await retrySessionRefresh();
    setRetrying(false);
    if (outcome === 'failed') toast.error("Still can't reach jflo.");
  };

  return (
    <SessionUnreachableNotice retrying={retrying} onRetry={() => void retry()} className={className} />
  );
}
